import React, { useContext, useEffect, useState } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import * as styles from './modal.styles';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { MainAxisAlignment } from '../../../core/components/flex/flex.component';
import Row from '../../../core/components/row/row.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import { getPermittedUserDeletionImpact, type PermittedUserDeletionImpact } from '../services/users.services';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';

interface Props {
  readonly showModal: boolean;
  /** `OfficeUser.Id` of the person being removed — how the impact is addressed. */
  readonly officeUserId?: string;
  readonly email: string;
  readonly handleClose: () => void;
  /** Called only once the impact, if any, has been acknowledged. */
  readonly handleAccept: (acknowledged: boolean, allOffices: boolean) => void;
}

/**
 * Confirms removing a permitted user, and says what it costs before it happens.
 *
 * Removing somebody from an office is not the small action the old dialog implied. Their access
 * goes, and with it every booking they still hold from today onwards — desks, seats in group
 * reservations, facilities. This dialog states the size of that in numbers and asks for an
 * explicit acknowledgement, the same shape the room deletion uses.
 *
 * When the same person also belongs to other offices, a second checkbox removes them from every
 * one this administrator may reach, in one action. Ticking it re-asks the server for the numbers
 * rather than adding anything up here: which offices are in reach is an authorisation question,
 * and the counts are what is being agreed to. The acknowledgement survives the refetch: ticking
 * the second box must not undo the first.
 */
const DeletePermittedUserModal = ({ showModal, officeUserId, email, handleClose, handleAccept }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const keycloak = useKeycloak();

  const [impact, setImpact] = useState<PermittedUserDeletionImpact | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [allOffices, setAllOffices] = useState(false);
  /**
   * Held apart from `impact` so the checkbox does not vanish while its own refetch is in flight —
   * a control that disappears the moment it is clicked moves every button underneath it.
   */
  const [otherOfficeCount, setOtherOfficeCount] = useState(0);

  // Opening is what resets the dialog, not fetching — the fetch runs again whenever the
  // all-offices box is toggled, and resetting there would clear the answer the administrator is
  // reading and blank the office count the checkbox itself is labelled with.
  useEffect(() => {
    if (showModal) {
      setImpact(undefined);
      setFailed(false);
      setAcknowledged(false);
      setOtherOfficeCount(0);
    }
  }, [showModal, officeUserId]);

  // Cleared on close rather than alongside the reset above. Both effects run in the same commit,
  // and the fetch below would still be holding the previous session's `true` from its own closure
  // — so reopening a dialog whose box had been ticked would fire an all-offices request that is
  // immediately superseded. Clearing it on the way out means it is already false on the way in.
  useEffect(() => {
    if (!showModal) {
      setAllOffices(false);
    }
  }, [showModal]);

  useEffect(() => {
    // Guards against a late answer landing on a closed dialog, or on one reopened for a different
    // person — which would show the previous colleague's numbers under this one's name.
    let current = true;

    if (showModal && officeUserId != null) {
      setLoading(true);
      setFailed(false);

      getPermittedUserDeletionImpact(officeUserId, keycloak, undefined, allOffices)
        .then((result) => {
          if (!current) return;
          setImpact(result);

          if (result !== undefined) {
            setOtherOfficeCount(result.otherOfficeCount);
          }

          // A failed count is reported as a failure, never substituted with zero: "nothing will be
          // cancelled" and "we could not find out" are different sentences, and only one of them
          // makes it safe to press delete.
          setFailed(result === undefined);
        })
        .catch(() => {
          if (current) setFailed(true);
        })
        .finally(() => {
          if (current) setLoading(false);
        });
    }

    return (): void => {
      current = false;
    };
  }, [showModal, officeUserId, keycloak, allOffices]);

  const hasImpact = impact?.hasImpact ?? false;
  const confirmable = !loading && !failed && (!hasImpact || acknowledged);

  const renderImpact = (): React.JSX.Element => {
    // Only the first count shows a spinner in place of the numbers. A refetch keeps the last ones on
    // screen, acknowledgement checkbox and all, so ticking the all-offices box does not make the
    // checkbox above it vanish and come back; Delete waits for the new numbers either way.
    if (loading && impact === undefined) {
      return <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />;
    }

    if (failed || impact === undefined) {
      return <span className={style(styles.error)}>{t('delete-permitted-user-impact-unavailable')}</span>;
    }

    // Reported in both branches, for the same reason `led` is: this removal releases nothing in
    // the other offices, and an administrator reading "nothing will be cancelled" needs to know
    // that is a statement about this office and not about the person's access to the product.
    // `otherOffices` can be shorter than `otherOfficeCount` when some of those offices belong to
    // another company and may not be named, so the count leads and the names follow if there are
    // any.
    // With the box on, only the offices the removal will actually reach may be named here — the
    // rest are named by the sentence about what is being left alone, and listing them under
    // "included in this removal" would contradict it. Matched by id against the not-permitted set
    // rather than by count, since both lists are filtered to this company and can be short.
    const notPermittedIds = new Set(impact.otherOfficesNotPermitted.map((office) => office.officeId));

    const namedOffices = allOffices
      ? impact.otherOffices.filter((office) => !notPermittedIds.has(office.officeId))
      : impact.otherOffices;

    // Nothing to include is not "0 office(s) are included" — it is the plain statement that this
    // removal does not reach them, which is what the unticked sentence already says.
    const includesOtherOffices = allOffices && impact.otherOfficesRemovable > 0;

    const otherOffices = impact.otherOfficeCount > 0 && (
      <Column>
        <span className={style(styles.bodyText)}>
          {includesOtherOffices
            ? t('delete-permitted-user-other-offices-included', { offices: impact.otherOfficesRemovable })
            : t('delete-permitted-user-other-offices', { offices: impact.otherOfficeCount })}
        </span>
        {namedOffices.length > 0 && (
          <span className={style(styles.bodyText)}>
            {namedOffices.map((office) => office.name).join(', ')}
          </span>
        )}
      </Column>
    );

    // Groups this colleague leads alone are deleted by the removal, and their bookings are cancelled
    // for everybody on them. Called out on its own line and in the warning style: it is the only thing
    // in this dialog that costs other people their days, and an administrator should not discover it
    // afterwards. It also makes the removal impactful, so the acknowledgement below is required.
    const dissolved = impact.groupsDissolved > 0 && (
      <span className={style(styles.warningText)}>
        {t('delete-permitted-user-impact-dissolved-groups', {
          groups: impact.groupsDissolved,
          bookings: impact.groupReservationsCancelledWithDissolvedGroups,
        })}
      </span>
    );

    // The rest of the groups they lead: somebody else leads those too, so they carry on without this
    // member. Reported in both branches — nothing is cancelled on their account, but an administrator
    // still wants to know which groups are losing a leader.
    const led = impact.groupReservationsLed > 0 && (
      <span className={style(styles.bodyText)}>
        {t('delete-permitted-user-impact-led-groups', { groups: impact.groupReservationsLed })}
      </span>
    );

    if (!impact.hasImpact) {
      return (
        <Column>
          <span className={style(styles.bodyText)}>{t('delete-permitted-user-no-impact')}</span>
          {led}
          {otherOffices}
        </Column>
      );
    }

    return (
      <Column>
        {/* Named anything but `count`, so i18next resolves the key as written instead of looking
            for plural variants of it that the translation files do not carry. */}
        {impact.bookings > 0 && (
          <span className={style(styles.bodyText)}>
            {t('delete-permitted-user-impact-bookings', { bookings: impact.bookings, days: impact.days })}
          </span>
        )}
        {impact.groupReservations > 0 && (
          <span className={style(styles.bodyText)}>
            {t('delete-permitted-user-impact-groups', { groups: impact.groupReservations })}
          </span>
        )}
        {impact.facilityReservations > 0 && (
          <span className={style(styles.bodyText)}>
            {t('delete-permitted-user-impact-facilities', { facilities: impact.facilityReservations })}
          </span>
        )}
        {dissolved}
        {led}
        {otherOffices}
        <span className={style(styles.bodyText)}>{t('delete-permitted-user-impact-history')}</span>
        <label className={style(styles.bodyText)} htmlFor="delete-permitted-user-acknowledge">
          <input
            id="delete-permitted-user-acknowledge"
            type="checkbox"
            checked={acknowledged}
            onChange={(event): void => {
              setAcknowledged(event.target.checked);
            }}
          />
          {t('delete-permitted-user-acknowledge')}
        </label>
      </Column>
    );
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyle(isMobile)}>
      <Column>
        <span className={style(styles.bodyText)}>
          {impact?.officeName
            ? t('about-to-remove-from-office', { email, office: impact.officeName })
            : `${t('about-to-remove')} ${email}`}
        </span>

        {renderImpact()}

        {/* Outside renderImpact deliberately: this checkbox triggers the refetch, and rendering it
            inside would make it disappear behind the spinner it just started. */}
        {otherOfficeCount > 0 && (
          <label className={style(styles.bodyText)} htmlFor="delete-permitted-user-all-offices">
            <input
              id="delete-permitted-user-all-offices"
              type="checkbox"
              checked={allOffices}
              disabled={loading}
              // The acknowledgement is kept: the administrator has already agreed to bookings being
              // cancelled, and the numbers refetched here are shown above it before they confirm.
              onChange={(event): void => setAllOffices(event.target.checked)}
            />
            {t('delete-permitted-user-all-offices', { offices: otherOfficeCount })}
          </label>
        )}

        {loading && impact !== undefined && <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />}

        {/* Said before the removal rather than after it: an administrator who cannot finish the
            offboarding needs to know while they can still ask somebody who can. */}
        {allOffices && impact != null && impact.otherOfficesNotPermittedCount > 0 && (
          <Column>
            <span className={style(styles.bodyText)}>
              {t('delete-permitted-user-all-offices-not-permitted', {
                offices: impact.otherOfficesNotPermittedCount,
              })}
            </span>
            {/* Names on their own line, and only the ones that may be named — the list is filtered
                to this company, so it can legitimately be empty while the count is not. */}
            {impact.otherOfficesNotPermitted.length > 0 && (
              <span className={style(styles.bodyText)}>
                {impact.otherOfficesNotPermitted.map((office) => office.name).join(', ')}
              </span>
            )}
          </Column>
        )}

        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              fullWidth
              disabled={!confirmable}
              onClick={(): void => {
                handleAccept(hasImpact, allOffices);
              }}
            >
              {t('delete')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleClose}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
    </Modal>
  );
};

export default DeletePermittedUserModal;
